import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Lock, Truck, CalendarClock, Check, Stethoscope, MessageCircle, Headphones } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getProduct } from '../lib/api';
import { PLANS, planMonthlyPrice, planSavings, formatMoney } from '../lib/pricing';
import { BASE } from '../lib/api';

const CATEGORY_QUIZ = {
  weightLoss: 'weight-loss',
};
const CATEGORY_SELECTION = {
  weightLoss: '/weight-loss/treatments',
};

export default function Checkout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState('3mo');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Contact form (pre-fill from quiz if available)
  const [form, setForm] = useState({
    name: '', email: '', phone: '', state: '',
    address1: '', address2: '', city: '', zip: '',
    card: '', exp: '', cvc: '', brand: '',
    consent: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getProduct(slug);
        if (cancelled) return;
        setProduct(p);
        // Try pre-fill from any category quiz
        const variant = CATEGORY_QUIZ[p.category_key];
        if (variant) {
          try {
            const raw = localStorage.getItem(`alfa_quiz_${variant}`);
            const parsed = raw ? JSON.parse(raw) : null;
            const c = parsed?.answers?.contact || {};
            setForm((f) => ({
              ...f,
              name: c.name || f.name,
              email: c.email || f.email,
              phone: c.phone || f.phone,
              state: parsed?.answers?.state || f.state,
            }));
          } catch { /* ignore */ }
        }
      } catch (e) {
        setError('Treatment not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const base = product?.monthly_price || 0;
  const monthly = useMemo(() => planMonthlyPrice(base, plan), [base, plan]);
  const months = PLANS.find((p) => p.id === plan)?.months || 3;
  const firstChargeTotal = monthly * months;

  const detectBrand = (num) => {
    const s = (num || '').replace(/\s|-/g, '');
    if (/^4/.test(s)) return 'Visa';
    if (/^5[1-5]/.test(s)) return 'Mastercard';
    if (/^3[47]/.test(s)) return 'Amex';
    if (/^6(?:011|5)/.test(s)) return 'Discover';
    return '';
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === 'card' ? { brand: detectBrand(v) } : {}) }));

  const valid = form.name && /.+@.+\..+/.test(form.email) && form.state && form.address1 && form.city && form.zip &&
                /^\d{12,19}$/.test((form.card || '').replace(/\s|-/g, '')) &&
                /^(0[1-9]|1[0-2])\/\d{2}$/.test(form.exp || '') &&
                /^\d{3,4}$/.test(form.cvc || '') && form.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const variant = CATEGORY_QUIZ[product.category_key];
      const cardDigits = form.card.replace(/\s|-/g, '');
      const payload = {
        product_slug: product.slug,
        plan,
        monthly_price: monthly,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        state: form.state.trim().toUpperCase().slice(0, 2),
        address_line1: form.address1.trim(),
        address_line2: form.address2.trim() || null,
        city: form.city.trim(),
        zip: form.zip.trim(),
        card_last4: cardDigits.slice(-4),
        card_brand: form.brand || null,
        card_exp: form.exp,
        quiz_variant: variant,
      };
      const res = await fetch(`${BASE}/api/orders/intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Order failed');
      }
      const data = await res.json();
      navigate(`/checkout/${slug}/confirmation?order=${data.order_id}`);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <ShellMessage msg="Loading checkout…" />;
  if (error && !product) return <ShellMessage msg={error} />;
  if (!product) return null;

  const backTo = CATEGORY_SELECTION[product.category_key] || '/';

  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="telegra-bg" aria-hidden="true" />
      <Header />

      <main className="flex-1 px-4 sm:px-6 pt-[96px] sm:pt-[128px] pb-16 sm:pb-24 max-w-[1200px] mx-auto w-full">
        <Link to={backTo} className="inline-flex items-center gap-2 text-[13.5px] uppercase tracking-[0.14em] font-semibold text-neutral-950 hover:text-neutral-950">
          <ArrowLeft size={15} /> Back to treatments
        </Link>

        {/* 3-step progress */}
        <div className="mt-6 flex items-center gap-3 sm:gap-4 text-[11.5px] sm:text-[12.5px] uppercase tracking-[0.16em] font-semibold">
          <div className="flex items-center gap-2 text-emerald-700">
            <span className="w-6 h-6 rounded-full bg-emerald-600 text-white inline-flex items-center justify-center"><Check size={13} strokeWidth={3} /></span>
            <span className="hidden sm:inline">Health Profile</span>
          </div>
          <span className="flex-1 h-px bg-neutral-300" />
          <div className="flex items-center gap-2 text-neutral-950">
            <span className="w-6 h-6 rounded-full bg-neutral-900 text-white inline-flex items-center justify-center text-[11px]">2</span>
            <span className="hidden sm:inline">Shipping</span>
          </div>
          <span className="flex-1 h-px bg-neutral-300" />
          <div className="flex items-center gap-2 text-neutral-950">
            <span className="w-6 h-6 rounded-full border border-neutral-300 text-neutral-950 inline-flex items-center justify-center text-[11px]">3</span>
            <span className="hidden sm:inline">Payment</span>
          </div>
        </div>

        <p className="mt-8 uppercase text-[12.5px] tracking-[0.24em] text-neutral-950">
          Step 3 of 3 — Submit for provider review
        </p>
        <h1 className="mt-2 font-display uppercase text-[34px] sm:text-[46px] leading-[0.98] tracking-[-0.015em] text-[color:var(--alfa-navy)]">
          Complete your order
        </h1>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          {/* LEFT — plan, address, payment */}
          <div className="space-y-8">
            {/* Plan selector — 4 tiers (1mo · 3mo · 6mo · 12mo) with strike-through & badges */}
            <section className="rounded-md border border-neutral-900/10 bg-white/85 backdrop-blur p-5 sm:p-6">
              <h2 className="font-display uppercase text-[18px] text-[color:var(--alfa-navy)]">Choose Your Plan</h2>
              <p className="mt-1 text-[13.5px] text-neutral-950">Prices shown per month. Longer plans have a lower monthly rate.</p>
              <div className="mt-4 grid grid-cols-1 gap-2.5">
                {PLANS.map((pl) => {
                  const m         = planMonthlyPrice(base, pl.id);
                  const oneMoRate = planMonthlyPrice(base, '1mo');
                  const showStrike = pl.id !== '1mo' && m < oneMoRate;
                  const saving    = planSavings(base, pl.id);
                  const active    = plan === pl.id;
                  return (
                    <button
                      type="button"
                      key={pl.id}
                      data-testid={`plan-${pl.id}`}
                      onClick={() => setPlan(pl.id)}
                      className={`relative text-left px-4 py-4 rounded-lg border-2 transition-all ${
                        active
                          ? 'border-neutral-950 bg-neutral-950 text-white shadow-lg'
                          : 'border-neutral-200 bg-white hover:border-neutral-500'
                      }`}
                    >
                      {pl.badge && (
                        <span className={`absolute -top-2 left-4 text-[9.5px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm ${
                          pl.id === '12mo'
                            ? (active ? 'bg-emerald-500 text-white' : 'bg-emerald-600 text-white')
                            : (active ? 'bg-amber-400 text-neutral-950' : 'bg-amber-500 text-white')
                        }`}>{pl.badge}</span>
                      )}
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className={`font-display uppercase text-[15px] leading-none ${active ? 'text-white' : 'text-neutral-950'}`}>
                            {product?.name?.split(' ')[0]} {pl.label}
                          </p>
                          <p className={`mt-1.5 text-[11.5px] uppercase tracking-[0.18em] ${active ? 'text-white/70' : 'text-neutral-950'}`}>
                            {pl.note}
                          </p>
                          {saving > 0 && (
                            <p className={`mt-1.5 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-sm ${
                              active ? 'bg-white/15 text-white' : 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                            }`}>
                              Total savings: {formatMoney(saving)}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {showStrike && (
                            <p className={`text-[12px] line-through ${active ? 'text-white/50' : 'text-neutral-400'}`}>
                              {formatMoney(oneMoRate)}
                            </p>
                          )}
                          <p className="flex items-baseline gap-1 justify-end">
                            <span className={`font-display text-[24px] leading-none ${active ? 'text-white' : 'text-neutral-950'}`}>{formatMoney(m)}</span>
                            <span className={`text-[12px] ${active ? 'text-white/70' : 'text-neutral-950'}`}>/mo</span>
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Contact + shipping */}
            <section className="rounded-md border border-neutral-900/10 bg-white/85 backdrop-blur p-5 sm:p-6">
              <h2 className="font-display uppercase text-[18px] text-[color:var(--alfa-navy)]">Shipping details</h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full name" testid="ck-name" value={form.name} onChange={(v) => setField('name', v)} required />
                <Field label="Email" testid="ck-email" type="email" value={form.email} onChange={(v) => setField('email', v)} required />
                <Field label="Phone" testid="ck-phone" value={form.phone} onChange={(v) => setField('phone', v)} />
                <Field label="State (2-letter)" testid="ck-state" value={form.state} onChange={(v) => setField('state', v.toUpperCase().slice(0,2))} required maxLength={2} />
                <Field label="Address" testid="ck-addr1" value={form.address1} onChange={(v) => setField('address1', v)} required wide />
                <Field label="Apt / Suite" testid="ck-addr2" value={form.address2} onChange={(v) => setField('address2', v)} wide />
                <Field label="City" testid="ck-city" value={form.city} onChange={(v) => setField('city', v)} required />
                <Field label="ZIP" testid="ck-zip" value={form.zip} onChange={(v) => setField('zip', v)} required />
              </div>
            </section>

            {/* Payment */}
            <section className="rounded-md border border-neutral-900/10 bg-white/85 backdrop-blur p-5 sm:p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-display uppercase text-[18px] text-[color:var(--alfa-navy)]">Payment</h2>
                <span className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.16em] text-neutral-950">
                  <Lock size={12} /> Secure
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_120px_100px] gap-3">
                <Field label={form.brand ? `Card number · ${form.brand}` : 'Card number'} testid="ck-card"
                       value={form.card}
                       onChange={(v) => setField('card', formatCard(v))}
                       required maxLength={23} placeholder="1234 1234 1234 1234" />
                <Field label="Exp (MM/YY)" testid="ck-exp"
                       value={form.exp}
                       onChange={(v) => setField('exp', formatExp(v))}
                       required maxLength={5} placeholder="MM/YY" />
                <Field label="CVC" testid="ck-cvc"
                       value={form.cvc}
                       onChange={(v) => setField('cvc', v.replace(/[^0-9]/g, '').slice(0,4))}
                       required maxLength={4} placeholder="CVC" />
              </div>
              <div className="mt-4 flex items-start gap-3 rounded-md bg-emerald-50 border border-emerald-100 p-3">
                <ShieldCheck size={16} className="text-emerald-700 mt-0.5" />
                <p className="text-[13px] text-emerald-900 leading-relaxed">
                  <strong>You will not be charged today.</strong> Your payment method will be authorized. The charge will be completed only after a licensed provider reviews your information and approves your treatment.
                </p>
              </div>
              <label className="mt-4 flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="ck-consent"
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-neutral-300"
                  checked={form.consent}
                  onChange={(e) => setField('consent', e.target.checked)}
                />
                <span className="text-[13px] text-neutral-950">
                  I acknowledge that this is a prescription treatment plan, that the final treatment, dosage, and formulation may be adjusted by a licensed provider, and I authorize ALFÀ to process my payment upon provider approval.
                </span>
              </label>
              {error && <p className="mt-3 text-[13px] text-red-600" data-testid="ck-error">{error}</p>}
              <button
                type="submit"
                disabled={!valid || submitting}
                data-testid="ck-submit"
                className={`mt-5 w-full inline-flex items-center justify-center gap-2 uppercase text-[14px] tracking-[0.14em] font-semibold px-6 py-4 rounded-sm transition-colors ${
                  valid && !submitting
                    ? 'bg-neutral-900 hover:bg-neutral-800 text-white'
                    : 'bg-neutral-300 text-neutral-950 cursor-not-allowed'
                }`}
              >
                {submitting ? 'Submitting…' : 'Submit for Provider Review'}
              </button>
            </section>
          </div>

          {/* RIGHT — order summary */}
          <aside className="lg:sticky lg:top-6 self-start rounded-md border border-neutral-900/10 bg-white/90 backdrop-blur p-5 sm:p-6 h-fit">
            <h2 className="font-display uppercase text-[18px] text-[color:var(--alfa-navy)]">Order summary</h2>
            <div className="mt-4 flex items-start gap-3 border-b border-neutral-900/10 pb-4">
              {product.img && (
                <img src={product.img} alt={product.name} className="w-16 h-16 object-cover rounded-sm bg-neutral-100" />
              )}
              <div className="min-w-0">
                <p className="font-display text-[16px] text-neutral-950 leading-tight">{product.name}</p>
                <p className="mt-1 text-[12.5px] uppercase tracking-[0.14em] text-neutral-950">{product.form}</p>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-[14px] text-neutral-950">
              <Row label={`Monthly price (${PLANS.find(p=>p.id===plan)?.label})`} value={`${formatMoney(monthly)}/mo`} strong />
              <Row label="Clinician review" value="Included" />
              <Row label="Shipping" value="Included, discreet" />
              <Row label="Commitment" value={`${months} month${months>1?'s':''}`} />
            </div>
            <div className="mt-4 border-t border-neutral-900/10 pt-4 space-y-2 text-[14px]">
              <Row label="First charge (after approval)" value={formatMoney(firstChargeTotal)} />
              <Row label="Billed" value={months === 1 ? 'Once, then monthly' : `Every ${months} months`} />
            </div>
            <div className="mt-5 space-y-2 text-[12px] text-neutral-950 leading-relaxed">
              <p className="flex items-start gap-2"><Truck size={13} className="mt-0.5 shrink-0" /> Ships from a U.S.-licensed compounding pharmacy in discreet packaging.</p>
              <p className="flex items-start gap-2"><CalendarClock size={13} className="mt-0.5 shrink-0" /> Provider review typically completes within 24–48 hours.</p>
              <p className="flex items-start gap-2"><ShieldCheck size={13} className="mt-0.5 shrink-0" /> HIPAA-compliant. Cancel any time before the next cycle.</p>
            </div>
          </aside>
        </form>

        {/* What Happens Next + Security + Help — full-width strip below form */}
        <section className="mt-14 rounded-md border border-neutral-900/10 bg-white/85 backdrop-blur p-6 sm:p-8">
          <p className="uppercase text-[11.5px] tracking-[0.24em] text-neutral-950 font-semibold">What Happens Next</p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { icon: Check,         t: 'Confirmation',               d: 'You receive a confirmation after you submit.' },
              { icon: Stethoscope,   t: 'Provider review',            d: 'A licensed U.S. provider reviews your health information.' },
              { icon: CalendarClock, t: 'Prescription, if appropriate', d: 'If a prescription is issued, it is sent to the partner pharmacy.' },
              { icon: Truck,         t: 'Ships in 2–3 business days',  d: 'Discreet, temperature-safe packaging to your door.' },
            ].map(({ icon: I, t, d }) => (
              <div key={t} className="flex items-start gap-3">
                <I size={18} className="text-neutral-900 shrink-0 mt-0.5" strokeWidth={1.6} />
                <div>
                  <p className="text-[14px] font-semibold text-neutral-950 leading-snug">{t}</p>
                  <p className="mt-1 text-[12.5px] text-neutral-950 leading-snug">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-md border border-neutral-900/10 bg-white/70 backdrop-blur px-5 py-4">
          <div className="flex flex-wrap items-center gap-4 text-[12.5px] text-neutral-950 font-semibold">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-600" /> HIPAA Secure</span>
            <span className="inline-flex items-center gap-1.5"><Lock size={14} className="text-neutral-900" /> 256-bit Encryption</span>
            <span className="inline-flex items-center gap-1.5"><Stethoscope size={14} className="text-neutral-900" /> Licensed U.S. Providers</span>
            <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> No hidden fees</span>
          </div>
        </section>

        <section className="mt-6 rounded-md border border-neutral-900/10 bg-white/70 backdrop-blur p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="uppercase text-[11.5px] tracking-[0.24em] text-neutral-950 font-semibold">Need help?</p>
            <p className="mt-1 text-[14.5px] text-neutral-950">Our care team is available to answer your questions.</p>
          </div>
          <div className="flex items-center gap-4 text-[13px] text-neutral-900 font-medium">
            <a href="mailto:help@alfameds.com" className="inline-flex items-center gap-1.5 hover-underline"><MessageCircle size={14} /> Email</a>
            <a href="sms:+19419489940" data-testid="checkout-sms-link" className="inline-flex items-center gap-1.5 hover-underline"><MessageCircle size={14} /> SMS</a>
            <a href="tel:+19419489940" data-testid="checkout-call-link" className="inline-flex items-center gap-1.5 hover-underline"><Headphones size={14} /> Call</a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, testid, value, onChange, type = 'text', required, maxLength, placeholder, wide }) {
  return (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[12px] uppercase tracking-[0.16em] text-neutral-950">{label}{required && ' *'}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3.5 py-2.5 text-[15px] text-neutral-900 focus:border-neutral-900 focus:outline-none"
      />
    </label>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-neutral-950">{label}</span>
      <span className={strong ? 'font-display text-neutral-950 text-[16px]' : 'text-neutral-900'}>{value}</span>
    </div>
  );
}

function ShellMessage({ msg }) {
  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="telegra-bg" aria-hidden="true" />
      <Header />
      <main className="flex-1 px-6 py-24 max-w-3xl mx-auto text-center">
        <p className="text-[16px] text-neutral-950">{msg}</p>
        <Link to="/" className="mt-6 inline-flex text-neutral-950 underline">Back to home</Link>
      </main>
      <Footer />
    </div>
  );
}

function formatCard(v) {
  const digits = v.replace(/[^0-9]/g, '').slice(0, 19);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExp(v) {
  const d = v.replace(/[^0-9]/g, '').slice(0, 4);
  if (d.length < 3) return d;
  return `${d.slice(0,2)}/${d.slice(2)}`;
}
